import { useSWR } from '@tiger/swr';
import { Button } from 'antd';
import { FC, useCallback } from 'react';
import Toast from 'components/Toast';
import { usePlaidLink } from 'components/vendor/plaid';
import security from 'stores/security';
import API from 'utils/API';
import { printf } from 'utils/i18n';
import style from './style.scss';

interface PlaidRenewModalContentProps {
    dayCount: number;
    fundCardId: number;
    redirectUri: string;
    close: () => void;
}

const PlaidRenewModalContent: FC<PlaidRenewModalContentProps> = ({ dayCount, fundCardId, redirectUri, close }) => {
    const { loading: sdkLoading, error: sdkError, open, loadSdk } = usePlaidLink();

    const { data, error, isLoading, isValidating, mutate } = useSWR<{ public_token: string }>(
        {
            url: API.TRADE.funding.getLinkToken(),

            params: {
                update_model: true,
                fund_card_id: fundCardId,
                customer_id: security.currentCustomerId,
                redirect_uri: redirectUri
            }
        },
        {
            dataChain: resp => resp.data
        }
    );

    const handleExit = useCallback(
        (error, metadata) => {
            if (error != null) {
                // 拼接错误信息和request_id（提供给plaid用来检查绑卡失败的原因）
                Toast.show(
                    `${error.error_message} ${metadata?.request_id ? `(request_id: ${metadata.request_id})` : ''}`
                );
            }

            close();
        },
        [close]
    );

    const handleConfirm = () => {
        if (error) {
            mutate();

            return;
        }

        if (sdkError) {
            loadSdk();

            return;
        }

        if (data?.public_token && !sdkLoading && !sdkError) {
            open({
                token: data?.public_token,
                redirect_uri: redirectUri,
                onSuccess: close,
                onExit: handleExit
            });
        }
    };

    return (
        <div>
            <div className={style.text}>
                {printf(
                    __(
                        'Your account authentication will expire in %s days. After expiration, you will not be able to use this account for deposits and withdrawals.'
                    ),
                    dayCount
                )}
            </div>
            <div className={style.footer}>
                <Button
                    size="large"
                    type="primary"
                    loading={sdkLoading || isLoading || isValidating}
                    onClick={handleConfirm}>
                    {error || sdkError ? __('Retry') : __('Authentication renewal')}
                </Button>
                <Button size="large" onClick={() => close()}>
                    {__('Cancel')}
                </Button>
            </div>
        </div>
    );
};

export default PlaidRenewModalContent;
